import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router';
import apiClient from '../../utils/axios';
import { fetchMemberById, getCachedMember } from '../../utils/memberCache';
import { useAuth } from '../auth/AuthContext';
import { logEvent } from '../../utils/analytics';
import TextMessage from '../common/messages/TextMessage';
import ImageMessage from '../common/messages/ImageMessage';
import AudioMessage from '../common/messages/AudioMessage';
import VideoMessage from '../common/messages/VideoMessage';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import '../../styles/MessagePage.css';

const MessagePage = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // GA 的開啟來源：推播點進來時網址帶 ?src=push；從置頂成員點進來時 state 帶 favorite
  const openSource = searchParams.get('src') === 'push'
    ? 'push'
    : (location.state?.src === 'favorite' ? 'favorite' : 'home');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false); // 初始狀態設為 false
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // 下一頁（較舊訊息）的游標；null = 還沒載過第一頁
  const [olderCursor, setOlderCursor] = useState(null);
  const [memberData, setMemberData] = useState(null); // 存儲成員資料（頁面對話對象，作為預設頭像）
  const [senderAvatars, setSenderAvatars] = useState({}); // 群組帳號訊息：senderMemberId -> 頭像
  const messagesPerPage = 10;
  
  // 獲取用戶信息以取得暱稱
  const { user } = useAuth();
  
  // Refs
  const hasMoreMessages = useRef(true);
  const isLoadingRef = useRef(false);
  const searchInputRef = useRef(null);
  const effectRanRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  // 每次重新載入第一頁（換成員、換搜尋詞）就遞增；舊請求回來時比對不符就丟棄，
  // 避免舊搜尋的結果被接到新列表後面
  const requestGenerationRef = useRef(0);
  const loaderRef = useRef(null);
  // 成員所屬團體，供 GA 事件使用（不放進 fetchPage 依賴，避免重抓）
  const memberGroupRef = useRef(undefined);
  const observerRef = useRef(null);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const debounceTimeout = useRef(null);

  // 游標分頁：GET /members/:id/messages/timeline，cursor 為 null 時載入最新一頁。
  // 頁與頁不會因為途中有新訊息進來而重疊或錯位（舊的 offset 分頁會）。
  const fetchPage = useCallback(async (cursor) => {
    // 重新載入第一頁時，捨棄還在進行中的舊請求；載入下一頁則避免重複請求
    if (cursor === null) {
      requestGenerationRef.current += 1;
    } else if (isLoadingRef.current) {
      return;
    }
    const generation = requestGenerationRef.current;

    // 設置載入狀態
    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ limit: String(messagesPerPage) });
    if (cursor) params.set('before', cursor);
    if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
    const apiUrl = `/members/${memberId}/messages/timeline?${params}`;

    try {
      const response = await apiClient.get(apiUrl);
      if (generation !== requestGenerationRef.current) return;

      const data = response.data;
      if (!data || !Array.isArray(data.messages)) {
        throw new Error('回應格式不正確');
      }

      hasMoreMessages.current = Boolean(data.hasOlder);

      if (cursor === null) {
        setMessages(data.messages);
      } else {
        setMessages(prev => [...prev, ...data.messages]);
      }

      // 回傳空頁時沿用原游標
      setOlderCursor(prev => data.olderCursor ?? prev);
    } catch (err) {
      if (generation !== requestGenerationRef.current) return;

      // 檢查是否為 401 未授權錯誤（用戶未登入）
      if (err.response && err.response.status === 401) {
        // 用戶未登入，重定向到登入提示頁面
        logEvent('guest_blocked', { member_id: Number(memberId), group: memberGroupRef.current });
        navigate('/login-required');
        return;
      }
      
      // 檢查是否為 403 權限錯誤
      if (err.response && err.response.status === 403) {
        logEvent('permission_denied', { member_id: Number(memberId), group: memberGroupRef.current });
        // 用戶已從外部傳入，不需要再調用 useAuth
        if (user && !user.isActive && user.additionalInfo) {
          // 如果用戶已提交資料但未激活，重定向到等待審核頁面
          navigate('/pending');
        } else {
          // 其他原因的 403 錯誤，重定向到一般禁止頁面
          navigate('/forbidden');
        }
        return;
      }
      
      setError(true);
    } finally {
      if (generation === requestGenerationRef.current) {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }
  }, [memberId, messagesPerPage, debouncedSearchTerm, user, navigate]);

  // 載入下一頁（較舊的訊息）
  const fetchMessages = useCallback(() => {
    if (olderCursor) fetchPage(olderCursor);
  }, [fetchPage, olderCursor]);

  // 獲取成員資料
  useEffect(() => {
    const controller = new AbortController();

    const fetchMemberData = async () => {
      try {
        const response = await apiClient.get(`/members/${memberId}`, { signal: controller.signal });
        setMemberData(response.data);
        memberGroupRef.current = response.data.group;
        logEvent('member_open', { member_id: Number(memberId), group: response.data.group, source: openSource });
        if (openSource === 'push') {
          logEvent('push_open', { member_id: Number(memberId), group: response.data.group, type: 'message' });
        }
        // 使用成員名字設置頁面標題
        document.title = `${response.data.name}`;
      } catch (err) {
        if (err.name !== 'CanceledError') {
          console.error('無法獲取成員資料:', err);
          // 如果獲取失敗，使用通用標題
          document.title = '成員訊息';
          // 標記錯誤狀態並停止後續訊息載入
          setError(true);
          hasMoreMessages.current = false;
        }
      }
    };

    fetchMemberData();

    return () => {
      controller.abort();
    };
  }, [memberId, openSource]);

  // 群組帳號：訊息各自可能帶有 senderMemberId（實際發訊成員），與頁面對話對象不同時
  // 需另外查詢該成員頭像；一般訊息沒有此欄位則沿用頁面對話對象的頭像。
  useEffect(() => {
    const pageMemberId = Number(memberId);
    const idsToFetch = [...new Set(
      messages
        .map((m) => m.senderMemberId)
        .filter((id) => id != null && id !== pageMemberId && getCachedMember(id) === undefined)
    )];

    if (idsToFetch.length === 0) return;

    let cancelled = false;
    Promise.all(idsToFetch.map((id) => fetchMemberById(id))).then((members) => {
      if (cancelled) return;
      setSenderAvatars((prev) => {
        const next = { ...prev };
        idsToFetch.forEach((id, index) => {
          next[id] = members[index]?.avatar ?? null;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [messages, memberId]);

  // 處理成員變更
  useEffect(() => {
    // 保存當前的 timeout ref 到一個變數，以便在清理函數中使用
    const currentSearchTimeout = searchTimeoutRef.current;
    
    // 清除之前的搜尋和計時器
    if (currentSearchTimeout) {
      clearTimeout(currentSearchTimeout);
    }
    
    // 重置所有狀態
    isLoadingRef.current = false; // 確保重置載入狀態
    setLoading(false);
    setOlderCursor(null);
    setMessages([]);
    setSenderAvatars({});
    hasMoreMessages.current = true;
    effectRanRef.current = true;
    
    // 立即載入最新一頁
    setTimeout(() => {
      fetchPage(null);
    }, 0);
    
    return () => {
      if (currentSearchTimeout) {
        clearTimeout(currentSearchTimeout);
      }
    };
  }, [memberId, fetchPage]);

  // 使用防抖後的搜尋詞來觸發搜尋
  useEffect(() => {
    // 只有在 effectRanRef.current 為 true 時才執行，避免初始渲染時觸發
    if (!effectRanRef.current) return;
    
    // 重置狀態
    isLoadingRef.current = false;
    setLoading(false);
    setMessages([]); // 清空現有消息
    setOlderCursor(null);
    hasMoreMessages.current = true;
    
    // 直接從最新一頁開始搜尋，而不是依賴狀態更新
    fetchPage(null);
    
  }, [debouncedSearchTerm, fetchPage]);

  // 使用 IntersectionObserver 處理無限滾動
  useEffect(() => {
    if (loading || !hasMoreMessages.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingRef.current && hasMoreMessages.current) {
          fetchMessages();
        }
      },
      { 
        root: null,
        rootMargin: '0px 0px 200px 0px',
        threshold: 0.1
      }
    );

    observerRef.current = observer;

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, olderCursor, fetchMessages]);

  // 修正: 改為直接處理輸入變化，然後使用防抖來更新搜尋詞
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearchTerm(e.target.value);
    }, 500);
  };

  // 清除搜尋內容並重新聚焦輸入框
  const clearSearch = () => {
    setSearchTerm('');
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    setDebouncedSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // sticky 搜尋列為即時搜尋，送出時僅收起鍵盤
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const renderMessage = (message) => {
    const pageMemberId = Number(memberId);
    const senderId = message.senderMemberId;
    // 群組帳號：訊息實際發送者與頁面對話對象不同時，改用該成員自己的頭像；
    // 查無資料或未帶 senderMemberId 時，一律 fallback 回頁面對話對象的頭像。
    const avatarSrc = (senderId != null && senderId !== pageMemberId)
      ? (senderAvatars[senderId] ?? memberData?.avatar)
      : memberData?.avatar;
    const phoneImageSrc = memberData?.phoneImage;

    if (message.content && (message.content.includes('%%%') || message.content.includes('％％％')) && user && user.nickname) {
      message = {
        ...message,
        content: message.content.replace(/%%%|％％％/g, user.nickname)
      };
    }

    const commonProps = {
      message,
      memberId: pageMemberId,
      avatarSrc,
      phoneImageSrc
    };
    // uid 是全域唯一的訊息鍵；官方 id 在同一成員內也會重複，不能當 React key
    const key = message.uid ?? message.id;

    switch (message.messageType) {
      case 0: // 純文本訊息
        return <TextMessage key={key} {...commonProps} />;
      case 1: // 圖片訊息
        return <ImageMessage key={key} {...commonProps} />;
      case 3: // 音頻訊息
        return <AudioMessage key={key} {...commonProps} />;
      case 4: // 視頻訊息
        return <VideoMessage key={key} {...commonProps} />;
      default:
        return <TextMessage key={key} {...commonProps} />;
    }
  };

  return (
    <div>
      {/* 頂部常駐搜尋列 */}
      <div className="message-search-bar">
        <form onSubmit={handleSearchSubmit} className="message-search-field">
          <SearchIcon className="message-search-icon" aria-hidden="true" />
          <input
            ref={searchInputRef}
            type="text"
            className="message-search-input"
            placeholder="搜尋訊息內容"
            value={searchTerm}
            onChange={handleSearchChange}
            aria-label="搜尋訊息內容"
          />
          {searchTerm && (
            <button
              type="button"
              className="message-search-clear"
              onClick={clearSearch}
              aria-label="清除搜尋"
            >
              <CloseIcon fontSize="small" />
            </button>
          )}
        </form>
      </div>

      <Container className="messages-container">
        {loading && messages.length === 0 ? (
          <Row className="justify-content-center">
            <Col xs={12} className="text-center mt-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">載入中...</span>
              </div>
              <p className="mt-3">正在載入訊息...</p>
            </Col>
          </Row>
        ) : error ? (
          <div className="alert alert-warning">
            無法獲取訊息，請稍後再試。
          </div>
        ) : messages.length === 0 ? (
          <div className="alert alert-info">目前沒有訊息可以顯示。</div>
        ) : (
          <>
            {Array.isArray(messages) ? (
              messages.map(message => renderMessage(message))
            ) : (
              <div className="alert alert-danger">
                數據格式錯誤，無法顯示訊息。
              </div>
            )}
            
            <div 
              ref={loaderRef} 
              className="loader-trigger text-center py-3"
              style={{ visibility: hasMoreMessages.current ? 'visible' : 'hidden' }}
            >
              {loading && messages.length > 0 ? (
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">載入更多...</span>
                </div>
              ) : hasMoreMessages.current ? (
                <div className="invisible-trigger">載入更多</div>
              ) : (
                <div className="text-muted">沒有更多訊息</div>
              )}
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default MessagePage;